import { gql } from 'apollo-boost';
import { ApolloClient } from 'apollo-client';
import { ResultsIndicator } from 'shared/providers/IndicatorProvider/utils';
import { AnswersBySectionIdResponse } from 'donor/portal/pages/FeedbackDashboard/Feedback/api';

export const PUBLIC_REPORT_QUERY = gql`
  query publicReport($reportId: Int!, $publicAccessToken: String!) {
    publicReport(reportId: $reportId, publicAccessToken: $publicAccessToken) {
      dbId
      name
      programName
      portfolioName
      status
      stage
      year
      startDate
      endDate
      totalDonations
      totalCost
      npoName
      donorName
    }
  }
`;

export const PUBLIC_SERVICES_QUERY = gql`
  query publicReportServices($reportId: Int!, $publicAccessToken: String!) {
    publicReportServices(
      reportId: $reportId
      publicAccessToken: $publicAccessToken
    ) {
      dbId
      name
      description
      goal
      version
      indicators {
        edges {
          node {
            dbId
            name
            stageId
            customizable
            unit
            definitionStart
            definitionEnd
            initialForecastStart
            initialForecastEnd
            initialMeasurementStart
            initialMeasurementEnd
            finalForecastStart
            finalForecastEnd
            finalMeasurementStart
            finalMeasurementEnd
            isHuman
            definition
            stgName
            version
            impactReceiptText
          }
        }
      }
    }
  }
`;

export const PUBLIC_ANSWERS_AND_RESULTS_QUERY = gql`
  query publicAnswersAndResults($reportId: Int!, $publicAccessToken: String!) {
    allNpoReportAnswers(
      reportId: $reportId
      publicAccessToken: $publicAccessToken
    ) {
      edges {
        node {
          dbId
          text
          date1
          date2
          questionText
          question {
            dbId
            text
            section
          }
        }
      }
    }
    publicReportResults(
      reportId: $reportId
      publicAccessToken: $publicAccessToken
    ) {
      edges {
        node {
          dbId
          description
          forecastInitial
          forecastCurrent
          forecastVariance
          indicatorId
          measurementType
          successCriteria
          classification
          currentForecastFullClaim
          currentForecastFundedClaim
          enabled
          timeHorizon
          file
          npoIndicatorFile
          indicator {
            dbId
            name
            stageId
            customizable
            unit
            definitionStart
            definitionEnd
            initialForecastStart
            initialForecastEnd
            initialMeasurementStart
            initialMeasurementEnd
            finalForecastStart
            finalForecastEnd
            finalMeasurementStart
            finalMeasurementEnd
            isHuman
            definition
            stgName
            version
            impactReceiptText
          }
        }
      }
    }
  }
`;

export interface PublicReportData {
  dbId: number;
  name: string;
  programName: string;
  portfolioName: string;
  status: string;
  stage: string;
  year: number;
  startDate: string;
  endDate: string;
  totalDonations: number;
  totalCost: number;
  npoName: string;
  donorName: string;
}

export interface PublicAnswersAndResultsResponse
  extends AnswersBySectionIdResponse {
  publicReportResults: ResultsIndicator[];
}

export const getPublicReport = (
  client: ApolloClient<object>,
  reportId: string,
  publicAccessToken: string,
) => {
  return client.query<{ publicReport: PublicReportData }>({
    query: PUBLIC_REPORT_QUERY,
    variables: {
      reportId: parseInt(reportId, 10),
      publicAccessToken,
    },
    fetchPolicy: 'network-only',
  });
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const getPublicServices = (
  client: ApolloClient<object>,
  reportId: string,
  publicAccessToken: string,
) => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return client.query<{ publicReportServices: any[] }>({
    query: PUBLIC_SERVICES_QUERY,
    variables: {
      reportId: parseInt(reportId, 10),
      publicAccessToken,
    },
    fetchPolicy: 'network-only',
  });
};

export const getPublicAnswersAndResults = (
  client: ApolloClient<object>,
  reportId: string,
  publicAccessToken: string,
) => {
  return client.query<PublicAnswersAndResultsResponse>({
    query: PUBLIC_ANSWERS_AND_RESULTS_QUERY,
    variables: {
      reportId: parseInt(reportId, 10),
      publicAccessToken,
    },
    fetchPolicy: 'network-only',
  });
};
